import { Inject, Injectable } from '@angular/core';

import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { SerializedRouterStateSnapshot } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { AuthenticationFacade } from '@planone/bau365/data-access-authentication';
import * as CookieConsentActions from '@planone/shared/feature-cookie-consent';
import { DocumentRef } from '@planone/shared/util-browser-api';
import { CookiehandlingService } from '@planone/shared/util-cookie-handling';
import {
  enableLinkClickTracking,
  LinkClickTrackingPlugin,
  trackLinkClick,
} from '@snowplow/browser-plugin-link-click-tracking';
import { enableYouTubeTracking, YouTubeTrackingPlugin } from '@snowplow/browser-plugin-youtube-tracking';
import {
  disableAnonymousTracking,
  enableActivityTracking,
  enableAnonymousTracking,
  newTracker,
  setUserId,
  trackPageView,
  trackSelfDescribingEvent,
} from '@snowplow/browser-tracker';
import { PageViewEvent } from '@snowplow/browser-tracker-core';
import { CommonEventProperties, LinkClickEvent, SelfDescribingEvent } from '@snowplow/tracker-core';

import * as SnowplowAnalyticsActions from '../state/snowplow.actions';
import {
  SnowplowConfiguration,
  SnowplowEnableYoutubeTrackingParams,
  SnowplowQueueEvent,
} from '../state/snowplow.models';
import { SNOWPLOW_CONFIGURATION } from '../token/snowplow.tokens';

@Injectable()
export class SnowplowService {
  private trackedElements: HTMLElement[] = [];
  private eventQueue: SnowplowQueueEvent[] = [];

  constructor(
    @Inject(SNOWPLOW_CONFIGURATION) private snowplowServiceConfig: SnowplowConfiguration,
    private document: DocumentRef,
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private authenticationFacade: AuthenticationFacade,
    private cookieHandlingService: CookiehandlingService
  ) {}

  processQueue(): void {
    if (this.getCookieConsentStatus() !== undefined) {
      this.eventQueue.forEach((event: SnowplowQueueEvent) => {
        switch (event.method) {
          case 'pageView':
            this.pageView(event.value);
            break;
          case 'selfDescribingEvent':
            this.selfDescribingEvent(event.value);
            break;
          case 'linkClick':
            this.linkClick(event.value);
            break;
          case 'enableYoutubeTracking':
            this.enableYoutubeTracking(event.value);
            break;
          case 'setUserId':
            this.setUserId(event.value);
            break;
        }
      });

      this.eventQueue = [];
    }
  }

  initialize(configuration: SnowplowConfiguration): void {
    newTracker(configuration.trackerId, configuration.endpoint, {
      ...configuration.trackerConfig,
      plugins: [LinkClickTrackingPlugin(), YouTubeTrackingPlugin()],
      stateStorageStrategy: 'none',
      discoverRootDomain: true,
      anonymousTracking: {
        withSessionTracking: false,
        withServerAnonymisation: true,
      },
      resetActivityTrackingOnPageView: true,
    });

    enableLinkClickTracking({
      pseudoClicks: true,
      trackContent: true,
    });

    enableActivityTracking({
      minimumVisitLength: 10,
      heartbeatDelay: 10,
    });

    const targetNode = this.document.nativeDocument.querySelector('#body');

    if (targetNode) {
      new MutationObserver(() => this.linkClickObserverCallback()).observe(targetNode, {
        childList: true,
        attributes: true,
        subtree: true,
      });
    }
  }

  anonymize(status: boolean): void {
    if (this.getCookieConsentStatus() === undefined) {
      this.eventQueue.push({
        method: 'anonymize',
        value: status,
      });
    }

    if (status) {
      enableAnonymousTracking({
        options: {
          withSessionTracking: false,
          withServerAnonymisation: true,
        },
        stateStorageStrategy: 'none',
      });
    } else {
      disableAnonymousTracking({
        stateStorageStrategy: 'cookieAndLocalStorage',
      });
    }
  }

  pageView(event: PageViewEvent & CommonEventProperties) {
    if (this.getCookieConsentStatus() === undefined) {
      this.eventQueue.push({
        method: 'pageView',
        value: event,
      });
    } else {
      trackPageView(event, [this.snowplowServiceConfig.trackerId]);
    }
  }

  selfDescribingEvent(event: SelfDescribingEvent & CommonEventProperties): void {
    if (this.getCookieConsentStatus() === undefined) {
      this.eventQueue.push({
        method: 'selfDescribingEvent',
        value: event,
      });
    } else {
      trackSelfDescribingEvent(event, [this.snowplowServiceConfig.trackerId]);
    }
  }

  linkClick(event: LinkClickEvent & CommonEventProperties): void {
    if (this.getCookieConsentStatus() === undefined) {
      this.eventQueue.push({
        method: 'linkClick',
        value: event,
      });
    } else {
      trackLinkClick(event, [this.snowplowServiceConfig.trackerId]);
    }
  }

  enableYoutubeTracking(configuration: SnowplowEnableYoutubeTrackingParams): void {
    if (this.getCookieConsentStatus() === undefined) {
      this.eventQueue.push({
        method: 'enableYoutubeTracking',
        value: configuration,
      });
    } else {
      enableYouTubeTracking(configuration);
    }
  }

  setUserId(tokenSub?: string): void {
    if (this.getCookieConsentStatus() === undefined) {
      this.eventQueue.push({
        method: 'setUserId',
        value: tokenSub,
      });
    } else {
      setUserId(tokenSub, [this.snowplowServiceConfig.trackerId]);
    }
  }

  excludeSearchParams(url: string): string {
    const urlObject = new URL(url);
    const searchParams = urlObject.searchParams;

    this.snowplowServiceConfig.excludeSearchParams.forEach((paramName) => searchParams.delete(paramName));

    let modifiedUrl = urlObject.origin + urlObject.pathname;

    if (searchParams.toString() != '') {
      modifiedUrl = modifiedUrl + '?' + searchParams;
    }

    return modifiedUrl;
  }

  isExcludedFromClickTracking(url?: string): boolean {
    return this.snowplowServiceConfig.excludeLinkUrls.find((excludeURL) => url?.startsWith(excludeURL)) ? true : false;
  }

  private linkClickObserverCallback() {
    Array.from(this.document?.nativeDocument?.querySelectorAll('a') ?? [])
      .filter((element: any) => {
        const url = element.getAttribute('href');
        const filter = /^(tel:|mailto:|http(s)?:\/\/)(.+)$/g;
        const data = this.extractData(this.route.snapshot);

        /**
         * Filter trackable URLs by the following conditions:
         * - Only allow anchor tag clicks which do not have customTracking=true set on the DOM element
         * - Only allow tracking if current route is not custom tracked
         * - Only allow external hyperlink-references starting with "tel:", "mailto:" or "http(s)://"
         */
        return (
          !(data?.snowplow?.customTracking && !data?.snowplow?.customPageviewOnly) &&
          ((url && url.match(filter)) ||
            element.classList.contains('cc-allow') ||
            element.classList.contains('cc-deny')) &&
          element.getAttribute('customTracking') !== 'true'
        );
      })
      .forEach((element: any) => {
        if (!this.trackedElements.includes(element)) {
          if (element.classList.contains('cc-allow')) {
            element.addEventListener('click', (_: any) =>
              this.store.dispatch(CookieConsentActions.changed({ status: true }))
            );
          } else if (element.classList.contains('cc-deny')) {
            element.addEventListener('click', (_: any) =>
              this.store.dispatch(CookieConsentActions.changed({ status: false }))
            );
          } else {
            element.addEventListener('click', (event: any) => this.linkClickListener(event));
            element.addEventListener('mouseup', (event: any) => {
              if (event.which === 2) {
                this.linkClickListener(event);
              }
            });
          }

          this.trackedElements.push(element);
        }
      });
  }

  private linkClickListener(event: any) {
    const url = event.currentTarget.getAttribute('href');
    const content =
      event.currentTarget.getAttribute('analyticsLinkText') &&
      event.currentTarget.getAttribute('analyticsLinkText').length > 0
        ? event.currentTarget.getAttribute('analyticsLinkText')
        : event.currentTarget.innerHTML ?? '';
    const classes = <string[]>Array.from(event.currentTarget.classList);
    const id = event.currentTarget.getAttribute('id');
    const target = event.currentTarget.getAttribute('target');
    const linkType = event.currentTarget.getAttribute('analyticsLinkType') ?? 'EXTERNAL_LINK';

    if (url && !this.isExcludedFromClickTracking(url)) {
      const data = this.extractData(this.route.snapshot);
      const pageType = data?.analyticsPageType ?? this.getPageType(this.router.url);
      const additionalContext = event.currentTarget.getAttribute('analyticsContext')
        ? JSON.parse(event.currentTarget.getAttribute('analyticsContext'))
        : [];

      let exclude_analytics = true;

      this.authenticationFacade.excludeAnalytics$.subscribe((status) => (exclude_analytics = status)).unsubscribe();

      this.store.dispatch(
        SnowplowAnalyticsActions.linkClick({
          targetUrl: url,
          elementContent: content,
          elementClasses: classes,
          elementTarget: target,
          elementId: id,
          context: [
            ...additionalContext,
            {
              schema: 'iglu:com.planone/link_click_additions/jsonschema/1-0-0',
              data: {
                link_type: linkType,
                link_text: content,
                page_type: pageType,
                context: this.route.snapshot.params['fair_id'],
                exclude_analytics: exclude_analytics,
              },
            },
          ],
        })
      );
    }
  }

  private extractData(route: SerializedRouterStateSnapshot | ActivatedRouteSnapshot | undefined | null): any {
    let child = route?.root.firstChild;
    let data = {};

    if (child) {
      while (child?.firstChild) {
        child = child.firstChild;
        data = { ...data, ...child.data };
      }
    }

    return data;
  }

  private getPageType(url: string) {
    const urlSegments = new URL('http://test.local/' + url).pathname.split('/');
    const pageType = urlSegments[urlSegments.length - 1] ?? '';

    if (pageType.length === 0) {
      return 'home';
    }

    return 'unknown';
  }

  private getCookieConsentStatus() {
    const cookie = this.cookieHandlingService.read('CookieConsent');

    return cookie !== '' ? cookie === 'allow' : undefined;
  }
}
